import { useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Input } from '../components/ui/input';
import { Label } from '../components/ui/label';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { useAuth } from '../context/AuthContext';
import { authAPI } from '../lib/api';
import { toast } from 'sonner';
import { Eye, EyeOff, Loader2, User, Phone, Shield } from 'lucide-react';

const Login = () => {
  const navigate = useNavigate();
  const { login } = useAuth();
  const [loading, setLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [isAdminLogin, setIsAdminLogin] = useState(false);
  
  // Marketer login
  const [marketerData, setMarketerData] = useState({
    full_name: '',
    whatsapp: '',
  });
  
  // Admin login
  const [adminData, setAdminData] = useState({
    username: '',
    password: '',
  });

  const handleMarketerChange = (e) => {
    setMarketerData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleAdminChange = (e) => {
    setAdminData((prev) => ({
      ...prev,
      [e.target.name]: e.target.value,
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setLoading(true);

    try {
      const payload = isAdminLogin ? adminData : marketerData;
      const response = await authAPI.login(payload);
      const { token, user, role, bo_role } = response.data;
      
      // Back-office user (owner/sales_admin/accountant)
      if (role === 'backoffice') {
        localStorage.setItem('bo_token', token);
        localStorage.setItem('bo_user', JSON.stringify(user));
        toast.success(`Welcome back, ${user.full_name}!`);
        navigate('/backoffice');
        window.location.reload();
        return;
      }

      login(token, user, role);
      toast.success('Login successful!');
      
      if (role === 'admin') {
        navigate('/admin/dashboard');
      } else {
        navigate('/dashboard');
      }
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid credentials');
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen flex items-center justify-center bg-neutral-background px-4 py-12">
      <div className="w-full max-w-md">
        {/* Logo */}
        <Link to="/" className="flex items-center justify-center gap-2 mb-8">
          <div className="w-12 h-12 rounded-full bg-primary-900 flex items-center justify-center">
            <span className="text-white font-serif text-2xl font-semibold">M</span>
          </div>
          <span className="font-serif text-3xl font-medium text-primary-900">Medichi Tribe</span>
        </Link>

        <Card className="border-neutral-border shadow-card animate-fade-in">
          <CardHeader className="text-center pb-2">
            <CardTitle className="font-heading text-2xl text-primary-900">Welcome Back</CardTitle>
            <CardDescription className="text-neutral-text-muted">
              {isAdminLogin ? 'Admin login' : 'Enter your details to access your dashboard'}
            </CardDescription>
          </CardHeader>
          <CardContent>
            {/* Toggle between Marketer and Admin login */}
            <div className="flex gap-2 mb-6">
              <button
                type="button"
                onClick={() => setIsAdminLogin(false)}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                  !isAdminLogin 
                    ? 'bg-primary-900 text-white' 
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
                data-testid="marketer-tab"
              >
                <User className="h-4 w-4 inline mr-2" />
                Marketer
              </button>
              <button
                type="button"
                onClick={() => setIsAdminLogin(true)}
                className={`flex-1 py-2 px-4 rounded-lg text-sm font-medium transition-all ${
                  isAdminLogin 
                    ? 'bg-primary-900 text-white' 
                    : 'bg-neutral-100 text-neutral-600 hover:bg-neutral-200'
                }`}
                data-testid="admin-tab"
              >
                <Shield className="h-4 w-4 inline mr-2" />
                Admin
              </button>
            </div>

            <form onSubmit={handleSubmit} className="space-y-5" autoComplete="off">
              {!isAdminLogin ? (
                <>
                  {/* Marketer Login - Name + WhatsApp */}
                  <div className="space-y-2">
                    <Label htmlFor="full_name" className="text-sm font-medium text-primary-900">
                      Full Name
                    </Label>
                    <div className="relative">
                      <User className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
                      <Input
                        id="full_name"
                        name="full_name"
                        type="text"
                        placeholder="Enter your full name"
                        value={marketerData.full_name}
                        onChange={handleMarketerChange}
                        required
                        autoComplete="name"
                        className="h-11 rounded-xl border-neutral-border pl-10"
                        data-testid="login-fullname-input"
                      />
                    </div>
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="whatsapp" className="text-sm font-medium text-primary-900">
                      WhatsApp Number
                    </Label>
                    <div className="relative">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 h-5 w-5 text-neutral-400" />
                      <Input
                        id="whatsapp"
                        name="whatsapp"
                        type="tel"
                        placeholder="+234 800 000 0000"
                        value={marketerData.whatsapp}
                        onChange={handleMarketerChange}
                        required
                        autoComplete="tel"
                        className="h-11 rounded-xl border-neutral-border pl-10"
                        data-testid="login-whatsapp-input"
                      />
                    </div>
                  </div>
                </>
              ) : (
                <>
                  {/* Admin Login - Username + Password */}
                  <div className="space-y-2">
                    <Label htmlFor="username" className="text-sm font-medium text-primary-900">
                      Email or Username
                    </Label>
                    <Input
                      id="username"
                      name="username"
                      type="text"
                      placeholder="Enter your email or username"
                      value={adminData.username}
                      onChange={handleAdminChange}
                      required
                      className="h-11 rounded-xl border-neutral-border"
                      data-testid="login-username-input"
                    />
                  </div>

                  <div className="space-y-2">
                    <Label htmlFor="password" className="text-sm font-medium text-primary-900">
                      Password
                    </Label>
                    <div className="relative">
                      <Input
                        id="password"
                        name="password"
                        type={showPassword ? 'text' : 'password'}
                        placeholder="Enter your password"
                        value={adminData.password}
                        onChange={handleAdminChange}
                        required
                        className="h-11 rounded-xl border-neutral-border pr-10"
                        data-testid="login-password-input"
                      />
                      <button
                        type="button"
                        onClick={() => setShowPassword(!showPassword)}
                        className="absolute right-3 top-1/2 -translate-y-1/2 text-neutral-text-muted hover:text-primary-900"
                        data-testid="toggle-password-btn"
                      >
                        {showPassword ? <EyeOff className="h-5 w-5" /> : <Eye className="h-5 w-5" />}
                      </button>
                    </div>
                  </div>

                  <div className="flex items-center justify-end">
                    <Link
                      to="/forgot-password"
                      className="text-sm text-primary-700 hover:text-primary-900 font-medium"
                      data-testid="forgot-password-link"
                    >
                      Forgot password?
                    </Link>
                  </div>
                </>
              )}

              <Button
                type="submit"
                disabled={loading}
                className="w-full h-11 bg-primary-900 hover:bg-primary-800 text-white rounded-xl font-medium transition-all hover:scale-[1.02] active:scale-[0.98]"
                data-testid="login-submit-btn"
              >
                {loading ? (
                  <>
                    <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                    Signing in...
                  </>
                ) : (
                  'Sign In'
                )}
              </Button>
            </form>

            <div className="mt-6 text-center">
              <p className="text-neutral-text-muted text-sm">
                Don't have an account?{' '}
                <Link
                  to="/register"
                  className="text-primary-700 hover:text-primary-900 font-medium"
                  data-testid="register-link"
                >
                  Join the Tribe
                </Link>
              </p>
            </div>
          </CardContent>
        </Card>
      </div>
    </div>
  );
};

export default Login;
